<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'

import {
  ChevronDown,
  CloudDownload,
  ExternalLink,
  Hand,
  Loader2,
  RefreshCw,
  ShoppingBag,
  Trash2,
  Zap
} from 'lucide-vue-next'
import type { PartnerIntegration, Tablet } from '@/types/types'
import TabletOrderFlowModal from './TabletOrderFlowModal.vue'
import useTabletVersion from '../composables/tabletVersion'

const { isSupportConnection } = useLocalStorage()
const isIdentifying = ref(false)
const isRefreshing = ref(false)
const isRequestingUpdate = ref(false)
const hasRequestedUpdate = ref(false)
const isOrderFlowModalOpen = ref(false)
const isActionsOpen = ref(false)
const notification = useNotification()
const { hasPermissionBySlug } = useAccess()
const { nativeVersion, otaUpdateId, platformLabel: formatPlatform, versionBadge, versionLabel } = useTabletVersion()
const { t, locale } = useI18n()

const TRANSLATION_PREFIX = 'pages.marketplace.tabletsPage'

const TABLET_HELP_URL =
  'https://heypongohelp.zendesk.com/hc/fr/articles/21925971964305-Utiliser-l-application-tablette-en-magasin'

const canUpdate = hasPermissionBySlug('marketplace.update')
const canCreate = hasPermissionBySlug('marketplace.create') || canUpdate

const format = (date: Date, format): string => dayjs(date).format(format)
const UPDATE_REFRESH_DELAY_MS = 15000

const ping = (tabletId: number) => {
  axios.get(`/tablets/${tabletId}/ping`).then(() => setTimeout(() => emit('onRefresh'), 2000))
}

const emit = defineEmits<{
  (e: 'onConfigure', tablet: Tablet): void
  (e: 'onRefresh'): void
  (e: 'onDelete', tablet: Tablet): void
}>()

const props = defineProps<{ tablet: Tablet; isLoading: boolean }>()

const canRead = hasPermissionBySlug('marketplace.read')

const isButtonDisabled = (version?: string) => {
  if (!version) {
    return true
  }
  const split = version.split('.').map((e) => parseInt(e))
  const minorVersion = `${split[0]}${split[1]}`
  return Number(minorVersion) < 54
}

const identify = async (id: number) => {
  isIdentifying.value = true
  try {
    await axios.get(`tablets/${id}/identify`)
    notification.create('success', {
      text: t('pages.tablet.notification.identifyConfigSuccess'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.tablet.notification.identifyConfigError'),
      timeout: 2000
    })
  }
  isIdentifying.value = false
}

const refreshConfig = async (id: number) => {
  isRefreshing.value = true
  try {
    await axios.get(`tablets/${id}/refresh-config`)
    notification.create('success', {
      text: t('pages.tablet.notification.refreshConfigSuccess'),
      timeout: 2000
    })
  } catch {
    notification.create('error', {
      text: t('pages.tablet.notification.refreshConfigError'),
      timeout: 2000
    })
  }
  isRefreshing.value = null
}

watch(
  () => props.tablet.applicationVersion,
  () => {
    hasRequestedUpdate.value = false
  }
)

const canInstallUpdate = computed(
  () => props.tablet.canRemoteUpdate === true && props.tablet.versionStatus === 'outdated_ota'
)

const requestUpdate = async (id: number) => {
  isRequestingUpdate.value = true
  try {
    await axios.post(`tablets/${id}/request-update`)
    hasRequestedUpdate.value = true
    notification.create('success', {
      text: t('pages.tablet.notification.requestUpdateSuccess'),
      timeout: 4000
    })
    setTimeout(() => emit('onRefresh'), UPDATE_REFRESH_DELAY_MS)
  } catch {
    notification.create('error', {
      text: t('pages.tablet.notification.requestUpdateError'),
      timeout: 2000
    })
  }
  isRequestingUpdate.value = false
}

const versionStatusBadge = computed(() => versionBadge(props.tablet))
const displayedVersion = computed(() => nativeVersion(props.tablet))
const updateId = computed(() => otaUpdateId(props.tablet))

const fullVersion = computed(() => versionLabel(props.tablet))

const platformLabel = computed(() => formatPlatform(props.tablet))

type Tone = 'ok' | 'warn' | 'bad' | 'muted'

const toneClass: Record<Tone, string> = {
  ok: 'bg-green-500',
  warn: 'bg-orange-500',
  bad: 'bg-red-500',
  muted: 'bg-stone-300'
}

const relativeTime = (date?: Date | string | null): string | null => {
  if (!date) {
    return null
  }
  const diff = Math.min(0, dayjs(date).diff(dayjs()))
  const formatter = new Intl.RelativeTimeFormat(locale.value, { numeric: 'auto' })
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000000],
    ['month', 2592000000],
    ['day', 86400000],
    ['hour', 3600000],
    ['minute', 60000],
    ['second', 1000]
  ]
  const unit = units.find(([, ms]) => Math.abs(diff) >= ms)
  return unit ? formatter.format(Math.round(diff / unit[1]), unit[0]) : formatter.format(0, 'second')
}

const exactDate = (date?: Date | string | null): string | null => (date ? format(date as Date, 'L LT') : null)

const seenAt = computed(() => props.tablet.lastPingAt ?? props.tablet.lastLoginAt ?? null)

const seenLabel = computed(() => {
  const when = relativeTime(seenAt.value)
  return when ? t(`${TRANSLATION_PREFIX}.card.seen`, { when }) : t(`${TRANSLATION_PREFIX}.card.neverSeen`)
})

const seenTone = computed<Tone>(() => {
  if (!seenAt.value) {
    return 'bad'
  }
  const hours = dayjs().diff(dayjs(seenAt.value), 'hour')
  if (hours < 2) {
    return 'ok'
  }
  return hours < 24 ? 'warn' : 'bad'
})

const versionTone = computed<Tone>(() => {
  switch (props.tablet.versionStatus) {
    case 'up_to_date':
      return 'ok'
    case 'outdated_ota':
    case 'outdated_native':
      return 'warn'
    case 'unsupported':
      return 'bad'
    default:
      return 'muted'
  }
})

const linkLabel = computed(() => {
  if (!props.tablet.partnerDevice) {
    return t(`${TRANSLATION_PREFIX}.card.notLinked`)
  }

  const linked = t(`${TRANSLATION_PREFIX}.card.linkedTo`, { partner: props.tablet.partnerDevice.partner.name })
  const deviceId = activePartnerDevice.value?.partnerDeviceId

  return deviceId ? `${linked} · ${deviceId}` : linked
})

const linkTone = computed<Tone>(() => (props.tablet.partnerDevice ? 'ok' : 'muted'))

const activePartnerDevice = computed<PartnerIntegration | null>(() => {
  return props.tablet?.partnerDevice?.isActive &&
    (props.tablet?.partnerDevice?.type === 'pos' || props.tablet?.partnerDevice?.partner?.slug === 'shopify')
    ? props.tablet.partnerDevice
    : null
})

const isConsultationMode = computed(() => props.tablet.isCustomerProfileConsultationMode)

const detailRows = computed(() => {
  const rows: { label: string; value: string; exact: string | null }[] = []

  if (props.tablet.lastCheckinAt) {
    rows.push({
      label: t(`${TRANSLATION_PREFIX}.lastCheckinAt`),
      value: relativeTime(props.tablet.lastCheckinAt) ?? '-',
      exact: exactDate(props.tablet.lastCheckinAt)
    })
  }

  return rows
})
</script>

<template>
  <div class="flex flex-col justify-between rounded-lg border bg-white">
    <div class="flex grow flex-col p-4">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h4 class="truncate text-base font-semibold text-foreground">{{ tablet.storeName }}</h4>
          <p class="mt-1 flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
            <span>{{ t('pages.marketplace.tabletsPage.card.tabletNumber', { id: tablet.id }) }}</span>
            <template v-if="tablet.applicationVersion">
              <span aria-hidden="true">·</span>
              <AppTooltip :text="updateId ? fullVersion : null">
                <span :class="updateId && 'cursor-help'">{{ platformLabel }} {{ displayedVersion }}</span>
              </AppTooltip>
            </template>
          </p>
        </div>

        <AppTooltip v-if="tablet.partnerDevice" :text="tablet.partnerDevice.partner.name">
          <Avatar class="size-10 cursor-help border bg-white">
            <AvatarImage
              :src="tablet.partnerDevice.partner.logo"
              :alt="tablet.partnerDevice.partner.name"
              class="object-contain p-1.5"
            />
            <AvatarFallback>{{ tablet.partnerDevice.partner.name.slice(0, 2) }}</AvatarFallback>
          </Avatar>
        </AppTooltip>
      </div>

      <div class="mt-4 flex flex-col gap-2 border-t pt-3 text-sm">
        <div class="flex items-center gap-2">
          <span class="size-2 shrink-0 rounded-full" :class="toneClass[seenTone]" />
          <AppTooltip :text="exactDate(seenAt)">
            <span class="text-foreground" :class="seenAt && 'cursor-help'">{{ seenLabel }}</span>
          </AppTooltip>
          <button
            v-if="isSupportConnection"
            type="button"
            class="group/ping cursor-pointer text-muted-foreground"
            :aria-label="t('pages.marketplace.tabletsPage.lastPingAt')"
            @click="ping(tablet.id)"
          >
            <Zap class="size-4 transition-transform duration-300 group-hover/ping:scale-125" />
          </button>
        </div>

        <div v-if="versionStatusBadge" class="flex items-center gap-2">
          <span class="size-2 shrink-0 rounded-full" :class="toneClass[versionTone]" />
          <AppTooltip :text="versionStatusBadge.tooltip">
            <span class="text-foreground" :class="versionStatusBadge.tooltip && 'cursor-help'">
              {{ versionStatusBadge.label }}
            </span>
          </AppTooltip>
          <a
            v-if="versionTone !== 'ok' && versionTone !== 'muted'"
            :href="TABLET_HELP_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="text-muted-foreground hover:text-foreground"
            :aria-label="t('pages.marketplace.tabletsPage.card.helpLink')"
          >
            <ExternalLink class="size-3.5" />
          </a>
        </div>

        <div class="flex items-center gap-2">
          <span class="size-2 shrink-0 rounded-full" :class="toneClass[linkTone]" />
          <span class="text-foreground">{{ linkLabel }}</span>
        </div>

        <div v-if="isConsultationMode" class="flex items-center gap-2">
          <span class="size-2 shrink-0 rounded-full bg-orange-500" />
          <span class="text-foreground">{{ t('pages.marketplace.tabletsPage.card.consultationMode') }}</span>
        </div>
      </div>

      <div v-if="detailRows.length" class="mt-3 flex flex-col gap-1 border-t pt-3 text-xs text-muted-foreground">
        <div v-for="detail in detailRows" :key="detail.label" class="flex items-center justify-between gap-2">
          <span>{{ detail.label }}</span>
          <AppTooltip :text="detail.exact">
            <span class="text-foreground" :class="detail.exact && 'cursor-help'">{{ detail.value }}</span>
          </AppTooltip>
        </div>
      </div>
    </div>
    <TabletOrderFlowModal v-if="isSupportConnection" v-model:is-open="isOrderFlowModalOpen" :tablet="tablet" />
    <div class="border-t p-4">
      <Teleport to="body">
        <Transition
          enter-active-class="transition-opacity duration-150"
          enter-from-class="opacity-0"
          leave-active-class="transition-opacity duration-150"
          leave-to-class="opacity-0"
        >
          <div v-if="isActionsOpen" class="pointer-events-none fixed inset-0 z-40 bg-black/20" />
        </Transition>
      </Teleport>

      <component
        :is="isButtonDisabled(tablet.applicationVersion) ? 'AppTooltip' : 'div'"
        :text="t('pages.marketplace.tabletsPage.waitingForUpdate')"
        tooltip-position="top"
      >
        <div class="flex gap-2">
          <DropdownMenu v-model:open="isActionsOpen">
            <DropdownMenuTrigger as-child>
              <Button variant="outline" class="flex-1 justify-between bg-white">
                {{ t('pages.marketplace.tabletsPage.buttons.actions') }}
                <ChevronDown class="transition-transform duration-200" :class="isActionsOpen && 'rotate-180'" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" class="min-w-48">
              <DropdownMenuItem
                v-if="canRead"
                class="group/item"
                :disabled="isButtonDisabled(tablet.applicationVersion) || isRefreshing"
                @click="refreshConfig(tablet.id)"
              >
                <Loader2 v-if="isRefreshing" class="animate-spin" />
                <RefreshCw v-else class="transition-transform duration-300 group-hover/item:rotate-180" />
                {{ t('pages.marketplace.tabletsPage.buttons.refresh') }}
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="canRead"
                class="group/item wave-trigger"
                :disabled="isButtonDisabled(tablet.applicationVersion) || isIdentifying"
                @click="identify(tablet.id)"
              >
                <Loader2 v-if="isIdentifying" class="animate-spin" />
                <Hand v-else class="wave-icon origin-bottom" />
                {{ t('pages.marketplace.tabletsPage.buttons.identify') }}
              </DropdownMenuItem>
              <DropdownMenuItem
                v-if="canRead && canInstallUpdate"
                class="group/item"
                :disabled="isRequestingUpdate || hasRequestedUpdate"
                @click="requestUpdate(tablet.id)"
              >
                <Loader2 v-if="isRequestingUpdate || hasRequestedUpdate" class="animate-spin" />
                <CloudDownload v-else class="transition-transform duration-300 group-hover/item:-translate-y-0.5" />
                {{ t(`pages.marketplace.tabletsPage.buttons.${hasRequestedUpdate ? 'updatePending' : 'update'}`) }}
              </DropdownMenuItem>
              <DropdownMenuItem v-if="isSupportConnection" class="group/item" @click="isOrderFlowModalOpen = true">
                <ShoppingBag class="transition-transform duration-300 group-hover/item:scale-110" />
                {{ t('pages.marketplace.tabletsPage.buttons.diagnostic') }}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="group/item text-destructive focus:text-destructive"
                @click="emit('onDelete', tablet)"
              >
                <Trash2
                  class="transition-transform duration-300 group-hover/item:-translate-y-0.5 group-hover/item:rotate-12"
                />
                {{ t('global.words.delete') }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            v-if="canCreate"
            variant="default-primary"
            class="flex-1"
            :disabled="isButtonDisabled(tablet.applicationVersion) || isLoading"
            @click="emit('onConfigure', tablet)"
          >
            <Loader2 v-if="isLoading" class="animate-spin" />
            {{ t('pages.marketplace.tabletsPage.buttons.settings') }}
          </Button>
        </div>
      </component>
    </div>
  </div>
</template>

<style scoped>
@keyframes wave {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-20deg);
  }
  75% {
    transform: rotate(20deg);
  }
}

.wave-trigger:hover .wave-icon {
  animation: wave 0.5s ease-in-out 2;
}
</style>
